import { AxiosError } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";
import { dd } from "@/helpers/datadog";
import { GLOBALLY_HANDLED_ERRORS } from "@/constants/errors";
import { handleGlobalErrorMessage } from "@/helpers/error";

import { redirectOnAxiosErrorInterceptor } from "lib/auth/redirects-provider";

import { toast } from "react-hot-toast";
import throttle from "lodash/throttle";
import { SessionStorage } from "@/helpers/session-storage";
import {
  classifyAxiosFailure,
  NETWORK_ERROR_MESSAGE,
  REQUEST_TIMED_OUT_MESSAGE,
} from "@/api/interceptors/response/classify";

const throttledErrorToast = (errorMessage: string, _throttleTime?: number) => {
  const throttleTime = _throttleTime || 5000;

  return throttle(() => {
    toast.error(errorMessage, {
      duration: throttleTime,
    });
  }, throttleTime);
};

const throttledNetworkErrorToast = throttledErrorToast(NETWORK_ERROR_MESSAGE);

const throttledRequestTimedoutToast = throttledErrorToast(
  REQUEST_TIMED_OUT_MESSAGE
);

const isLocal = process.env.NEXT_PUBLIC_ENV === "local";

const REDACTED_BODY = "[redacted]";

type InterceptedAxiosErrorT = AxiosError<any> & {
  config?: AxiosInstanceInterceptorRequestExtensionI;
};

/**
 * The failure as it is logged. A request flagged `meta.redactBody` is logged
 * without its body, on its config and on the response's copy of it; the
 * status, URL and error code stay. The caller still gets the error as it
 * was.
 */
export const loggableAxiosError = (e: InterceptedAxiosErrorT) => {
  if (!e?.config?.meta?.redactBody) {
    return e;
  }

  const withoutBody = <T extends object | undefined>(config: T) =>
    config && { ...config, data: REDACTED_BODY };

  return Object.assign(Object.create(Object.getPrototypeOf(e)), e, {
    stack: e.stack,
    config: withoutBody(e.config),
    response: e.response && {
      ...e.response,
      config: withoutBody(e.response.config),
    },
  }) as InterceptedAxiosErrorT;
};

export const handleOnErrorInterceptor = (e: InterceptedAxiosErrorT) => {
  const SS = new SessionStorage();
  const url = `${e?.config?.baseURL}${e?.config?.url}`;
  const errorCode = e?.response?.data?.error_code as number;
  const errorContext = {
    data: {
      errorCode,
      error: loggableAxiosError(e),
      pageUrl: window?.location.href,
    },
  };

  if (
    errorCode &&
    Object.keys(GLOBALLY_HANDLED_ERRORS).includes(errorCode.toString())
  ) {
    handleGlobalErrorMessage(errorCode, "Failed to load data.");
  }

  redirectOnAxiosErrorInterceptor(e);

  const startedAt = e?.config?.meta?.requestStartedAt;
  if (!startedAt) console.log(`Execution time undefined`);

  const executionTime =
    new Date().getTime() - (e?.config?.meta?.requestStartedAt || 0);

  // Timeout is checked FIRST: a timed-out request also has no response, and
  // the old order labelled every slow server "You are offline" (GLE-8227).
  const { isTimedout, isNetworkError } = classifyAxiosFailure(e);

  if (isTimedout) {
    throttledRequestTimedoutToast();
    dd.error(`REQUEST TIMEDOUT ${executionTime}ms - ${url}`, errorContext);

    SS.lastNetworkErrorAt = new Date().toISOString();
  }

  if (isNetworkError) {
    throttledNetworkErrorToast();
    dd.error(`NETWORK ERROR - ${url}`, errorContext);

    /**
     * Store last network error at to show on Dialer badge of unstable network connection
     */
    SS.lastNetworkErrorAt = new Date().toISOString();
  }

  //github.com/axios/axios/issues/383#issuecomment-234079506
  const message = `Endpoint ${e?.response?.status || e?.code} ${
    e?.config?.method
  } ${url} ${executionTime}ms`;

  const status = e?.response?.status;
  const isExpectedStatus =
    !!status && !!e?.config?.meta?.expectedStatuses?.includes(status);

  if (!(isTimedout || isNetworkError || isExpectedStatus)) {
    dd.error(message, errorContext);
  }

  if (isLocal) {
    console.error(message, errorContext);
  }

  /**
   * Reject, always. An `onRejected` handler that returns a value fulfils the
   * promise, so react-query used to record a failed request as a success whose
   * `data` was the AxiosError - no isError, no onError, no retry. Which URLs
   * got a real rejection was decided by a hardcoded list, an axis that never
   * matched the question: it is the caller, not the endpoint, that knows
   * whether a failure is an error or a value. Callers that still want the
   * value opt in through `swallow` (lib/api/legacy-swallow.ts).
   */
  return Promise.reject(e);
};
